import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Anchor,
  Scale,
  Layers,
  Sparkles,
  Boxes,
} from "lucide-react";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const KIT_SLUGS = [
  "pack-cartons-demenagement-t1",
  "pack-cartons-demenagement-t2",
  "pack-cartons-demenagement-t3",
  "pack-cartons-demenagement-maison",
];

const Sections = ({ city, products = [] }) => {
  const kits = KIT_SLUGS
    .map((s) => products.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <>
      {/* ======= SECTION 2 — Livraison à domicile : 2 engagements ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons de déménagement à domicile à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Oubliez la corvée des déplacements dans les enseignes de
              bricolage périphériques et la lutte pour faire entrer des
              cartons encombrants dans votre voiture. Nous apportons vos
              fournitures directement à votre porte à {city.name}.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Acheminement 100% interne",
                body: "C'est notre propre société logistique qui charge et transporte vos produits en Île-de-France. Aucun intermédiaire ne manipule votre commande, ce qui nous permet de vous garantir un soin absolu et une ponctualité rigoureuse.",
                Icon: Truck,
              },
              {
                title: "Matériel prêt à l'action",
                body: "Voyageant au sec dans nos véhicules utilitaires étanches, vos cartons arrivent chez vous sans avoir pris l'humidité. Ils conservent ainsi leur rigidité maximale pour supporter vos charges les plus lourdes.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Kits tout-en-un (4 vrais kits) ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Boxes className="h-3.5 w-3.5" strokeWidth={2.6} />
              Prêts pour l&apos;action
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Découvrez nos kits tout-en-un
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour vous éviter de savants calculs ou le stress d&apos;oublier
              l&apos;essentiel, nos experts ont conçu des dotations globales
              calibrées selon vos besoins réels. Sélectionnez la taille de
              votre espace et recevez un écosystème complet.
            </p>
          </div>

          {/* Two benefit cards above the kits grid */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Des configurations sur mesure",
                body: "Du studio à la maison familiale, nos packs intègrent le volume idéal de cartons standards, de boîtes à livres compactes, de rouleaux d'adhésif technique et de film bulle protecteur.",
              },
              {
                title: "L'assurance du juste prix",
                body: "Commander une solution groupée vous permet de bénéficier d'un tarif global avantageux par rapport à l'achat d'accessoires au détail, tout en vous évitant les frais de livraison répétés en cas d'oubli.",
              },
            ].map(({ title, body }) => (
              <div
                key={title}
                className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>

          {/* The 4 kit products */}
          {kits.length === 0 ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-square bg-zinc-100 animate-pulse rounded-2xl"
                />
              ))}
            </div>
          ) : (
            <div
              className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
              data-testid="city-malakoff-kits"
            >
              {kits.map((p, idx) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  index={idx}
                  hideQty
                  goToProduct
                />
              ))}
            </div>
          )}

          <div className="mt-10">
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-malakoff-cta-packs"
            >
              <Package className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les kits
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Méthode pro 3 étapes (cartes "file folder" avec bandeau) ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
              La méthode pro
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Trois étapes pour réussir vos cartons à coup sûr
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Une fois vos fournitures réceptionnées, suivez cette feuille
              de route simple et logique pour optimiser la mise en boîte
              de vos affaires.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                n: "01",
                tag: "L'ancrage",
                title: "Le scellage de la base",
                body: "Ne croisez jamais les rabats du fond de vos cartons sans scotch, cela fragilise la structure. Appliquez deux bandes d'adhésif en croix sur le dessous pour créer une base capable de soutenir vos objets sans faillir.",
                Icon: Anchor,
                accent: "#FF7A00",
              },
              {
                n: "02",
                tag: "La loi du poids",
                title: "La répartition des densités",
                body: "Placez systématiquement les éléments les plus lourds (livres, dossiers, appareils ménagers) au fond du carton, et terminez par les objets légers (textiles, coussins) sur le dessus. Cela stabilise le module et protège votre dos lors du levage.",
                Icon: Scale,
                accent: "#237cb5",
              },
              {
                n: "03",
                tag: "L'amortissement",
                title: "Le calage des vides",
                body: "Un carton qui bouge est un carton en danger. Utilisez du film bulle ou du papier de calage pour combler les espaces vides à l'intérieur de la boîte. En éliminant l'air, vous empêchez les objets de s'entrechoquer et vous renforcez la résistance du carton face à l'empilement dans le camion.",
                Icon: Layers,
                accent: "#FF7A00",
              },
            ].map(({ n, tag, title, body, Icon, accent }) => (
              <article
                key={n}
                className="bg-white rounded-2xl border border-[#cfdbe6] overflow-hidden flex flex-col"
              >
                {/* Header band */}
                <div
                  className="flex items-center justify-between px-5 py-4"
                  style={{ backgroundColor: accent }}
                >
                  <div className="flex items-center gap-3 text-white">
                    <span className="font-display font-extrabold text-3xl tracking-tight text-white">
                      {n}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/90">
                      {tag}
                    </span>
                  </div>
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-white/15 text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                </div>
                {/* Body */}
                <div className="p-5 md:p-6 flex-1">
                  <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/produit/rouleau-adhesif"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-malakoff-cta-adhesif"
            >
              Voir le rouleau d&apos;adhésif
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/emballage-protection"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-malakoff-cta-calage"
            >
              <Layers className="h-4 w-4" strokeWidth={2.6} />
              Voir le papier bulle et le calage
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-malakoff-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
